import React, { useState, useCallback, useId } from "react";

import {
  EditableTaskPromptProps,
  PromptType,
  ChoiceOptions,
  MultipleChoiceOptions,
  TextOptions,
  NumericOptions,
  CheckboxOptions,
  ValidationConstraints,
} from "@/components/tasks/types";
import TaskPrompt from "../TaskPrompt";
import RichTextEditor from "@/components/widgets/richtext/RichTextEditor";
import { TaskPrompt as TaskPromptType } from "@/api/types";
import { PROMPT_COMPONENTS } from "../elements";

// Component
export default function EditableTaskPrompt({
  prompt,
  expanded = true,
  setExpanded,
  readOnly = false,
  showType = false,
  richTextDescription = true,
  onNameChange,
  onHeaderChange,
  onDescriptionChange,
  onTypeChange,
  onOptionsChange,
  onTypeAndOptionsChange,
}: EditableTaskPromptProps) {
  const [localName, setLocalName] = useState(prompt?.name);
  const [localHeader, setLocalHeader] = useState(prompt?.header);
  const [localDescription, setLocalDescription] = useState(prompt?.description);
  const [localType, setLocalType] = useState(prompt?.type);
  const [localOptions, setLocalOptions] = useState(prompt?.options || {});

  const handleNameChange = useCallback(
    (newName: string) => {
      setLocalName(newName);
      onNameChange?.(newName);
    },
    [onNameChange]
  );

  const handleHeaderChange = useCallback(
    (newHeader: string) => {
      setLocalHeader(newHeader);
      onHeaderChange?.(newHeader);
    },
    [onHeaderChange]
  );

  const handleDescriptionChange = useCallback(
    (newDescription: string) => {
      setLocalDescription(newDescription);
      onDescriptionChange?.(newDescription);
    },
    [onDescriptionChange]
  );

  const handleOptionsChange = useCallback(
    (newOptions: any) => {
      setLocalOptions(newOptions);
      onOptionsChange?.(newOptions);
    },
    [onOptionsChange]
  );

  const getCurrentValidation = useCallback((): ValidationConstraints => {
    const typedOptions = localOptions as any;
    return typedOptions?.validation || {};
  }, [localOptions]);

  const handleValidationChange = useCallback(
    (newValidation: ValidationConstraints) => {
      const updatedOptions = {
        ...localOptions,
        validation: newValidation,
      };
      setLocalOptions(updatedOptions);
      onOptionsChange?.(updatedOptions);
    },
    [localOptions, onOptionsChange]
  );

  const handleTypeChange = useCallback(
    (newType: PromptType, oldType: PromptType) => {
      setLocalType(newType);

      // Reset options when type changes, but only reset choices if the type is
      // changing from a type with incompatible options
      let newOptions = null;

      // Preserve existing validation when switching types
      const existingValidation = getCurrentValidation();

      // if oldType is convo-break, always clear the name since it was auto-generated
      if (oldType === "convo-break") {
        setLocalName("");
      }

      switch (newType) {
        case "single-line-textbox":
        case "multi-line-textbox":
          if (
            oldType !== "single-line-textbox" &&
            oldType !== "multi-line-textbox"
          ) {
            newOptions = {
              "max-characters": 0,
              validation: existingValidation,
            } as TextOptions;
          }
          break;
        case "numeric":
          if (oldType !== "numeric") {
            newOptions = {
              validation: existingValidation,
            } as NumericOptions;
          }
          break;
        case "multiple-choice":
          if (oldType !== "multiple-choice" && oldType !== "dropdown") {
            newOptions = {
              choices: [{ name: "", value: "" }],
              allowMultipleSelections: false,
              validation: existingValidation,
            } as MultipleChoiceOptions;
          }
          break;
        case "dropdown":
          if (oldType !== "multiple-choice" && oldType !== "dropdown") {
            newOptions = {
              choices: [{ name: "", value: "" }],
              validation: existingValidation,
            } as ChoiceOptions;
          }
          break;
        case "convo-break":
          // convo breaks get a name auto-generated from their UUID, and have no options
          handleNameChange(`convo-break-${prompt?.uuid}`);
          newOptions = {};
          break;
        case "checkbox":
        default:
          newOptions = {
            validation: existingValidation,
          } as CheckboxOptions;
          break;
      }

      if (newOptions !== null) {
        // if we have new options, set both the type and options
        setLocalOptions(newOptions);
        onTypeAndOptionsChange?.(newType, newOptions);
      } else {
        // just trigger the type change
        onTypeChange?.(newType);
      }
    },
    [
      onTypeChange,
      onOptionsChange,
      onTypeAndOptionsChange,
      getCurrentValidation,
      handleNameChange,
      prompt?.uuid,
    ]
  );

  const renderOptionsEditor = () => {
    const Component = PROMPT_COMPONENTS[localType as PromptType];
    
    if (!Component || !Component.Editor) {
      return null;
    }

    const Editor = Component.Editor;
    const validation = getCurrentValidation();

    return (
      <Editor
        options={localOptions as any}
        validation={validation}
        onOptionsChange={handleOptionsChange}
        onValidationChange={handleValidationChange}
      />
    );
  };

  const renderPromptPreview = () => {
    // use the TaskPrompt component to render the prompt preview
    const prompt = {
      name: localName,
      header: localHeader,
      description: localDescription,
      type: localType,
      options: localOptions,
    } as TaskPromptType;
    return <TaskPrompt prompt={prompt} readOnly={readOnly} />;
  };

  const header = (
    <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
    Prompt Configuration ({localType})
    </h4>
  )

  const expander = (
      <div className="p-0 text-2xl text-center text-gray-400 cursor-pointer" onClick={() => setExpanded?.(true)}>
        ...
      </div>
  )

  const promptNameField = (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Prompt Name
      </label>
      <input
        type="text"
        value={localName}
        onChange={(e) => handleNameChange(e.target.value)}
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Enter prompt name..."
      />
    </div>
  );

  const promptTypeField = (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Prompt Type
      </label>
      <select
        value={localType}
        onChange={(e) =>
          handleTypeChange(
            e.target.value as PromptType,
            localType as PromptType
          )
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      >
        <option value="single-line-textbox">Single Line Textbox</option>
        <option value="multi-line-textbox">Multi Line Textbox</option>
        <option value="numeric">Numeric Value</option>
        <option value="multiple-choice">Multiple Choice</option>
        <option value="checkbox">Checkbox</option>
        <option value="dropdown">Dropdown</option>
        <option value="convo-break">Conversation Break</option>
      </select>
    </div>
  );

  const configSection = (
    <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
      {header}

      {/* Name Field */}
      {promptNameField}

      {expanded ? (
        <>
          {/* Type Field */}
          {promptTypeField}

          {/* Header Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prompt Header
            </label>
            <p className="text-sm text-gray-400 mb-1">
              Optional header text that, if specified, replaces the prompt name
              as the header.
            </p>
            <input
              type="text"
              value={localHeader || ""}
              onChange={(e) => handleHeaderChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter prompt header..."
            />
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            {richTextDescription ? (
              <div className="space-y-2">
                <RichTextEditor
                  initialValue={localDescription}
                  ownerId={prompt?.id}
                  rows={6}
                  ownerType="prompt"
                  onChange={(value) => handleDescriptionChange(value)}
                />
              </div>
            ) : (
              <textarea
                value={localDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                placeholder="Enter prompt description..."
              />
            )}
          </div>

          {/* Options Configuration */}
          <div>
            <label className="block text-md font-medium text-gray-700 mb-1">
              Options
            </label>
            {renderOptionsEditor()}
          </div>
        </>
      ) : expander}
    </div>
  );

  const previewSection = (
    <div className="space-y-4 mt-4">
      <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
        Preview
      </h4>

      <div className="space-y-3">{renderPromptPreview()}</div>

      {/* Type indicator for development/debugging */}
      {showType && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
            Type: {localType}
          </span>
        </div>
      )}
    </div>
  );

  // Special case:
  // if the prompt type is "convo-break", we only render the type chooser and the preview section
  // the name is inferred from the uuid created for the prompt, and there are no options to configure
  if (localType === "convo-break") {
    return (
      <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
        <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
          {header}

          {/* Type Field */}
          {promptTypeField}

          { expanded ? previewSection : expander }
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
      {/* Editable Prompt Configuration */}
      {configSection}

      {/* Preview Section */}
      { expanded && previewSection }
    </div>
  );
}
